import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// VW Beetle — a rounded 1960s classic at true scale (1 unit = 1 metre):
// length ~4.08, width 1.54, height 1.50, wheelbase 2.40, on narrow tyres with
// big chrome hubcaps. Nose is +Z. The body is composed from rotation-free
// scaled spheres (rotation is applied after scale, so non-uniform scale never
// carries a rotation): a cabin dome with a wide base tangent to the narrow
// lower body, a low trunk rising through a merged cowl to the windscreen, and
// an engine lid whose crown meets the dome's rear edge and falls in ONE
// continuous arc to the rear bumper. Four separate bulbous fenders stand
// proud of the body, joined by low thin running boards. The glazing is flat:
// a raked windscreen and a tilted rear pane, each backed by a paint panel so
// no daylight shows behind them. Upright chrome headlamp buckets sit on the
// front fender crowns, tail lamps ride the rear fenders, one low chrome blade
// bumper with two overriders hugs each end. Body panels carry the "paint"
// recolour slot (pastel light blue); `drive` spins the wheels and `hazards`
// blinks the four fender indicators.

const PAINT = "#7fa8c9"; // pastel light blue — the "paint" slot default
const TRIM = "#17181b"; // running boards, underbody, valances
const WELL = "#141518"; // wheel-well discs
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // steel wheel discs
const TIRE = "#1a1b1d";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.31;
const TYRE_W = 0.15; // narrow classic tyres
const WHEEL_X = 0.7; // half track — tyre face proud of the fender bulge
const AXLE_F = 1.15;
const AXLE_R = -1.25; // wheelbase 2.40
const FENDER_X = 0.61; // fender crest line, well outboard of the 0.57 body

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). */
function blob(scale: Vec3, position: Vec3): Part {
  return {
    geometry: new THREE.SphereGeometry(1, 16, 12),
    color: PAINT,
    position,
    scale,
    slot: "paint",
  };
}

/** One classic steel wheel: narrow tyre, silver steel disc slightly recessed,
 *  and a big chrome hubcap (about half the wheel face) capped by a flattened
 *  chrome dome proud of the tyre. Every part shares `name` so the `drive`
 *  track spins them as one node. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  return [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.21, 0.21, 0.04, 16, SILVER, [x + side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.15, 0.15, 0.035, 16, CHROME, [x + side * 0.075, y, z], [0, 0, Math.PI / 2], { name }),
    {
      geometry: new THREE.SphereGeometry(0.07, 12, 8),
      color: CHROME,
      position: [x + side * 0.075, y, z] as Vec3,
      scale: [0.55, 1, 1] as Vec3,
      name,
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Fronts sit on the fender crests behind the headlamp buckets, rears on the
// fender tops beside the tail lamps; all are low chips sunk into the fender.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3 }> = [
  { name: "indicator-fl", pos: [-FENDER_X, 0.745, 1.24] },
  { name: "indicator-fr", pos: [FENDER_X, 0.745, 1.24] },
  { name: "indicator-rl", pos: [-FENDER_X, 0.72, -1.48] },
  { name: "indicator-rr", pos: [FENDER_X, 0.72, -1.48] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.04, 0.04, 1.34, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    // Dark well disc: proud of the fender bulge, behind the (prouder) tyre face.
    for (const s of S) P.push(cyl(0.33, 0.33, 0.02, 18, WELL, [s * 0.75, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  P.push(box(0.88, 0.14, 3.2, TRIM, [0, 0.24, -0.05])); // underbody pan, tucked inside the body

  // -- Rounded body masses (paint): the whole shell is blobs, no slab sides ----
  P.push(blob([0.57, 0.48, 1.48], [0, 0.58, -0.12])); // lower body — widest at the doors, tucking in at both axles
  P.push(blob([0.58, 0.64, 1.02], [0, 0.88, -0.12])); // cabin dome — wide-based egg arc, tangent to the body shoulder
  P.push(blob([0.5, 0.33, 0.84], [0, 0.5, 1.2])); // low trunk bonnet dipping between the fenders to the nose
  P.push(blob([0.5, 0.2, 0.55], [0, 0.78, 0.85])); // cowl — a long gentle rise from mid-bonnet to the screen base
  P.push(blob([0.54, 0.5, 0.85], [0, 0.56, -1.12])); // engine lid — crown at the dome's rear edge, one arc to the tail
  P.push(paint(0.88, 0.3, 0.46, [0, 0.38, 1.72])); // front valance between the fenders, under the trunk
  P.push(paint(0.88, 0.3, 0.4, [0, 0.38, -1.76])); // rear valance between the fenders, under the lid

  // -- Fenders: four separate bulbous lobes proud of the narrow body ----------
  for (const s of S) {
    P.push(blob([0.13, 0.3, 0.58], [s * FENDER_X, 0.44, AXLE_F]));
    P.push(blob([0.13, 0.3, 0.6], [s * FENDER_X, 0.44, AXLE_R]));
    P.push(box(0.16, 0.04, 1.3, TRIM, [s * 0.58, 0.3, -0.05])); // low thin running board at sill height
  }

  // -- Greenhouse: flat panes seated against the dome, each backed by paint ---
  // The paint backer panels fill the air gap between the flat glass and the
  // curved dome so no daylight shows behind a pane from any angle; the side
  // glass is a straight recessed band whose painted pillars sit proud of it.
  P.push(paint(0.6, 0.26, 0.08, [0, 1.14, 0.745], [-0.64, 0, 0])); // windscreen backer / surround
  P.push(box(0.56, 0.22, 0.05, SCREEN, [0, 1.15, 0.786], [-0.64, 0, 0])); // windscreen
  P.push(paint(0.48, 0.2, 0.07, [0, 1.19, -0.978], [0.7, 0, 0])); // rear window backer / surround
  P.push(box(0.44, 0.16, 0.04, GLASS, [0, 1.2, -1.005], [0.7, 0, 0])); // rear window, tilted on the rear face
  for (const s of S) {
    P.push(paint(0.09, 0.24, 0.12, [s * 0.31, 1.14, 0.76], [-0.64, 0, 0])); // A-pillar post
    P.push(box(0.06, 0.14, 0.93, GLASS, [s * 0.526, 1.09, -0.175], [0, 0, s * 0.25])); // side glass band
    P.push(paint(0.08, 0.17, 0.1, [s * 0.525, 1.085, 0.32], [0, 0, s * 0.25])); // front door pillar cap
    P.push(paint(0.1, 0.17, 0.08, [s * 0.53, 1.085, -0.2], [0, 0, s * 0.25])); // B-pillar
    P.push(paint(0.08, 0.17, 0.1, [s * 0.525, 1.085, -0.66], [0, 0, s * 0.25])); // rear quarter pillar cap
  }

  // -- Front fascia ------------------------------------------------------------
  for (const s of S) {
    // Upright chrome headlamp bucket standing on the fender crown with a
    // forward-facing glass eye proud of the bucket's front surface.
    P.push(cyl(0.08, 0.09, 0.11, 14, CHROME, [s * FENDER_X, 0.75, 1.38]));
    P.push(cyl(0.06, 0.06, 0.02, 12, LAMP, [s * FENDER_X, 0.755, 1.47], [Math.PI / 2, 0, 0]));
    P.push(cyl(0.035, 0.035, 0.02, 10, CHROME, [s * 0.735, 0.5, 1.3], [0, 0, Math.PI / 2])); // horn grille disc
    P.push(box(0.05, 0.16, 0.1, CHROME, [s * 0.38, 0.44, 2.02])); // overrider post on the blade
  }
  P.push(box(1.34, 0.07, 0.05, CHROME, [0, 0.42, 2.02])); // one low chrome blade hugging the nose
  P.push(cyl(0.05, 0.05, 0.022, 14, CHROME, [0, 0.6, 2.01], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.12, 0.025, 0.05, CHROME, [0, 0.745, 1.78])); // trunk handle
  P.push(box(0.04, 0.1, 0.06, TRIM, [0, 0.48, 2.01])); // plate bracket on the nose
  P.push(box(0.34, 0.12, 0.02, PLATE, [0, 0.52, 2.05])); // front plate above the bumper blade
  for (const s of S) P.push(box(0.18, 0.014, 0.02, TRIM, [s * 0.14, 1.1, 0.855], [-0.64, 0, 0])); // wipers at the glass base

  // -- Rear fascia -------------------------------------------------------------
  for (const s of S) {
    P.push(cyl(0.055, 0.055, 0.03, 12, CHROME, [s * FENDER_X, 0.68, -1.59], [Math.PI / 2, 0, 0])); // tail-lamp base
    P.push(cyl(0.045, 0.045, 0.05, 12, RED, [s * FENDER_X, 0.68, -1.625], [Math.PI / 2, 0, 0])); // tail lamp on the fender
    P.push(box(0.1, 0.016, 0.2, TRIM, [s * 0.13, 1.009, -1.45], [-0.26, 0, 0])); // louvre strip, near-coplanar with the lid
    P.push(box(0.05, 0.16, 0.1, CHROME, [s * 0.38, 0.44, -2.0])); // overrider post on the blade
  }
  P.push(box(1.34, 0.07, 0.05, CHROME, [0, 0.42, -2.0])); // one low chrome blade hugging the tail
  P.push(box(0.34, 0.12, 0.02, PLATE, [0, 0.51, -1.985])); // rear plate on the valance
  P.push(box(0.1, 0.04, 0.03, CHROME, [0, 0.575, -1.975])); // plate lamp hood on the lid tail
  P.push(box(0.28, 0.1, 0.22, TRIM, [0.18, 0.2, -1.82])); // muffler under the valance
  P.push(cyl(0.03, 0.03, 0.18, 10, CHROME, [0.25, 0.2, -1.93], [Math.PI / 2, 0, 0])); // single tailpipe

  // -- Furniture ----------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.06, 0.015, 0.015, TRIM, [s * 0.5, 0.97, 0.38])); // short dark mirror arm off the door top
    P.push(cyl(0.03, 0.03, 0.015, 10, TRIM, [s * 0.535, 0.985, 0.38], [Math.PI / 2, 0, 0])); // small dark mirror head
    P.push(box(0.02, 0.02, 0.1, CHROME, [s * 0.555, 0.7, 0.05])); // door handle
  }
  for (const ind of INDICATORS) {
    P.push(box(0.05, 0.03, 0.05, AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const vwBeetle = defineModel({
  id: "vw-beetle",
  name: "VW Beetle",
  batchId: "cars",
  tags: ["car", "volkswagen", "beetle", "classic", "compact"],
  summary: "A rounded 1960s VW Beetle at true scale, in pastel light blue.",
  description:
    "A 1960s Volkswagen Beetle at real 1 unit = 1 metre scale (about 4.08 " +
    "long, 1.54 wide, 1.50 tall, 2.40 wheelbase, on narrow tyres with big " +
    "chrome hubcaps). The signature egg profile is built from scaled spheres: " +
    "a wide-based cabin dome flowing into the narrow lower body, a low trunk " +
    "rising through a merged cowl, and an engine lid falling in one " +
    "continuous arc from the dome to the rear bumper, with four separate " +
    "bulbous fenders over the wheels and low running boards. Flat raked " +
    "windscreen and tilted rear pane, each set into a painted surround, " +
    "recessed side glass with painted pillars. Upright chrome headlamp " +
    "buckets on the front fender crowns, tail lamps on the rear fenders, " +
    "louvre strips on the engine lid, one low chrome blade bumper with " +
    "overriders per end, a nose badge disc and a single tailpipe. Body " +
    "panels carry the 'paint' recolour slot (pastel light blue); `drive` " +
    "spins the wheels and `hazards` blinks the four fender indicators.",
  author: "Fable 5",
  footprint: 2.2,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
